import { RiskAIApi } from '@Apis';
import { useOrgStore } from '@Models/useOrgModel';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { queryClient } from 'src/QueryClient';
import { CorrelatedExpression, CorrelatedRule } from './types';

const useRulesQueryKey = (orgId: string, hideSocialGraph?: boolean) => [
  'correlatedRules',
  orgId,
  hideSocialGraph,
];

export const useRiskAIRulesQuery = (params?: { hideSocialGraph?: boolean }) => {
  const { hideSocialGraph } = params || {};
  const { orgId } = useOrgStore();
  const queryKey = useRulesQueryKey(orgId, hideSocialGraph);
  return useQuery({
    queryKey,
    queryFn: () => RiskAIApi.getRules(orgId, hideSocialGraph),
    select: ({ data }) => data.filter((rule) => !!rule.raiRuleName),
  });
};

export const useRuleMutations = () => {
  const { t } = useTranslation();
  const { orgId } = useOrgStore();
  const queryKey = useRulesQueryKey(orgId);

  const createRuleMutation = useMutation({
    mutationFn: (newRule: CorrelatedRule) =>
      RiskAIApi.createRule(orgId, newRule),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey });
    },
    onError: () =>
      window.showNotification({
        severity: 'error',
        titleMsg: t('common.apiException.internalError'),
      }),
  });

  const editRuleMutation = useMutation({
    mutationFn: (updatedRule: CorrelatedRule) =>
      RiskAIApi.editRule(orgId, updatedRule),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey });
    },
    onError: (e: any) => {
      if (e?.data?.error?.details?.[0]?.message === 'RULE_ALREADY_EXISTS') {
        window.showNotification({
          severity: 'error',
          titleMsg: t('globalSetting.correlatedRules.ruleExists'),
        });
      } else {
        window.showNotification({
          severity: 'error',
          titleMsg: t('common.apiException.internalError'),
        });
      }
    },
  });

  const deleteRuleMutation = useMutation({
    mutationFn: (ruleId: string) => RiskAIApi.deleteRule(orgId, ruleId),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey });
    },
    onError: (e: any) => {
      if (e?.data?.error?.code === 'InvalidRequest') {
        window.showNotification({
          severity: 'error',
          titleMsg: t(
            'globalSetting.correlatedRules.policyUsingRuleError.title'
          ),
          bodyMsg: t('globalSetting.correlatedRules.policyUsingRuleError.body'),
        });
      } else {
        window.showNotification({
          severity: 'error',
          titleMsg: t('common.apiException.internalError'),
        });
      }
    },
  });

  return { createRuleMutation, editRuleMutation, deleteRuleMutation };
};

const useExpressionsQueryKey = (orgId: string) => [
  'correlatedExpressions',
  orgId,
];

export const useRiskAIExpressionsQuery = () => {
  const { orgId } = useOrgStore();
  const queryKey = useExpressionsQueryKey(orgId);
  return useQuery({
    queryKey,
    queryFn: () => RiskAIApi.getExpressions(orgId),
    select: ({ data }) =>
      data?.filter((exp) => !exp.raiExpMarkers?.includes('annotation')),
  });
};

export const useExpressionMutations = () => {
  const { t } = useTranslation();
  const { orgId } = useOrgStore();
  const queryKey = useExpressionsQueryKey(orgId);

  const createExpMutation = useMutation({
    mutationFn: (newExpression: CorrelatedExpression) =>
      RiskAIApi.createExpression(orgId, newExpression),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey });
    },
    onError: () =>
      window.showNotification({
        severity: 'error',
        titleMsg: t('common.apiException.internalError'),
      }),
  });

  const editExpMutation = useMutation({
    mutationFn: (updatedExpression: CorrelatedExpression) =>
      RiskAIApi.editExpression(orgId, updatedExpression),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey });
    },
    onError: (e: any) => {
      // TODO: update message when API is updated
      if (
        e?.data?.error?.details?.[0]?.message === 'EXPRESSION_ALREADY_EXISTS'
      ) {
        window.showNotification({
          severity: 'error',
          titleMsg: t('globalSetting.correlatedExpressions.expressionExists'), // TODO: L10N verify
        });
      } else {
        window.showNotification({
          severity: 'error',
          titleMsg: t('common.apiException.internalError'),
        });
      }
    },
  });

  const deleteExpMutation = useMutation({
    mutationFn: (expressionId: string) =>
      RiskAIApi.deleteExpression(orgId, expressionId),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey });
    },
    onError: (e: any) => {
      if (e?.data?.error?.code === 'InvalidRequest') {
        window.showNotification({
          severity: 'error',
          titleMsg: t(
            'globalSetting.correlatedExpressions.ruleUsingExpressionError.title'
          ),
          bodyMsg: t(
            'globalSetting.correlatedExpressions.ruleUsingExpressionError.body'
          ),
        });
      } else {
        window.showNotification({
          severity: 'error',
          titleMsg: t('common.apiException.internalError'),
        });
      }
    },
  });

  return { createExpMutation, editExpMutation, deleteExpMutation };
};

export const useRiskAIConditionsQuery = () => {
  const { orgId } = useOrgStore();
  const queryKey = ['correlatedExpressionConditions', orgId];
  return useQuery({
    queryKey,
    queryFn: () => RiskAIApi.getConditions(orgId),
    select: ({ data }) => data,
  });
};
